import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Paper, Grid, Button, IconButton, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Stack, Avatar, Divider, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Menu, MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaidIcon from '@mui/icons-material/Paid';
import CancelIcon from '@mui/icons-material/Cancel';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import api from '../../services/api';
import BoothSelectionMap from '../vendor/BoothSelectionMap';
import {
  getVendorContactEmail,
  getVendorIdentityLines,
  isAdminPlaceholderVendor,
} from '../../utils/vendorContact';
import AdminVendorRegistration from './AdminVendorRegistration';
import BoothChip from '../common/BoothChip';

// Vendor uploads are served as static files from /uploads (a sibling of /api).
// Trim the trailing /api off the configured axios base URL so the browser
// fetches the photos from the right origin in every environment.
const PHOTO_ORIGIN = (api.defaults.baseURL || '').replace(/\/api\/?$/, '');
const resolvePhotoUrl = (photo) => {
  if (!photo) return '';
  if (photo.url?.startsWith('http')) return photo.url;
  return `${PHOTO_ORIGIN}${photo.url}`;
};

const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
};

// Kept in sync with ApprovalsQueueTable so the deny reason picker matches
// across the dashboard, the approvals queue, and this details page.
const DENIAL_REASONS = [
  'Booth no longer available',
  'Incomplete information',
  'Did not meet criteria',
  'Duplicate registration',
  'Other',
];

const sectionCardSx = {
  p: 3,
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  height: '100%',
  bgcolor: 'background.paper',
};

const SectionHeader = ({ icon, title }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
    {icon}
    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
  </Box>
);

const Field = ({ label, value, href }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      {href ? (
        <Typography variant="body1" component="a" href={href} target="_blank" rel="noopener noreferrer"
          sx={{ display: 'block', color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
          {value}
        </Typography>
      ) : (
        <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>{value}</Typography>
      )}
    </Box>
  );
};

const statusChipColor = (status) => {
  switch (status) {
    case 'approved':
    case 'complete':
      return 'success';
    case 'pending_approval':
    case 'pending':
      return 'warning';
    case 'denied':
      return 'error';
    default:
      return 'default';
  }
};

const paymentChipColor = (status) => {
  switch (status) {
    case 'succeeded':
      return 'success';
    case 'pending':
      return 'warning';
    case 'failed':
    case 'canceled':
      return 'error';
    default:
      return 'default';
  }
};

const VendorDetailsPage = () => {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [relatedVendors, setRelatedVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsError, setPaymentsError] = useState('');
  const [refundLoading, setRefundLoading] = useState({});
  const [success, setSuccess] = useState('');
  const [emailHistory, setEmailHistory] = useState([]);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [emailsError, setEmailsError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  const [editOpen, setEditOpen] = useState(false);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');

  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);

  const [approveOpen, setApproveOpen] = useState(false);
  const [approveMarkAsPaid, setApproveMarkAsPaid] = useState(false);
  const [approveNotes, setApproveNotes] = useState('');
  const [denyOpen, setDenyOpen] = useState(false);
  const [denyReason, setDenyReason] = useState(DENIAL_REASONS[0]);
  const [denyNotes, setDenyNotes] = useState('');
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenNotes, setReopenNotes] = useState('');
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [decisionError, setDecisionError] = useState('');
  const [changeBoothDialog, setChangeBoothDialog] = useState(null);
  const [assignPaidDialogOpen, setAssignPaidDialogOpen] = useState(false);
  const [swapLoading, setSwapLoading] = useState(false);
  const [assignPaidLoading, setAssignPaidLoading] = useState(false);

  const ACTIVE_BOOTH_STATUSES = ['soft_held', 'pending_approval', 'approved', 'complete'];

  const fetchEmailHistory = useCallback(async () => {
    setEmailsLoading(true);
    setEmailsError('');
    try {
      const response = await api.get(`/admin/vendors/${vendorId}/emails`);
      setEmailHistory(response.data);
    } catch (err) {
      setEmailsError('Failed to fetch email history');
      console.error('Error fetching email history:', err);
    } finally {
      setEmailsLoading(false);
    }
  }, [vendorId]);

  const refetchVendor = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const vendorRes = await api.get(`/admin/vendors/${vendorId}`);
      const mainVendor = vendorRes.data;
      if (!mainVendor) {
        setError('Vendor not found');
        setLoading(false);
        return;
      }
      setRelatedVendors(Array.isArray(mainVendor.related_vendors) ? mainVendor.related_vendors : []);
      setVendor(mainVendor);
      setLoading(false);
      await fetchEmailHistory();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch vendor details');
      setLoading(false);
    }
  }, [vendorId, fetchEmailHistory]);

  useEffect(() => {
    refetchVendor();
  }, [refetchVendor]);

  const handleResendConfirmation = async () => {
    setResendLoading(true);
    try {
      const response = await api.post(`/admin/vendors/${vendorId}/resend-confirmation`);
      setSuccess(response.data.message);
      await fetchEmailHistory();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend confirmation email');
    } finally {
      setResendLoading(false);
    }
  };

  useEffect(() => {
    const fetchPayments = async () => {
      if (!vendor) return;
      setPaymentsLoading(true);
      setPaymentsError('');
      try {
        const vendorIds = [vendor.id, ...relatedVendors.map((v) => v.id)];
        const paymentPromises = vendorIds.map((id) =>
          api.get(`/admin/vendors/${id}/payments`)
        );
        const paymentResponses = await Promise.all(paymentPromises);
        const allPayments = paymentResponses.flatMap((res) => res.data);
        setPayments(allPayments.sort((a, b) =>
          new Date(b.created_at) - new Date(a.created_at)
        ));
      } catch (err) {
        setPaymentsError(err.response?.data?.error || 'Failed to fetch payments');
      } finally {
        setPaymentsLoading(false);
      }
    };
    fetchPayments();
  }, [vendor, relatedVendors, success]);

  const handleRefund = async (paymentId) => {
    setRefundLoading((prev) => ({ ...prev, [paymentId]: true }));
    try {
      await api.post(`/admin/payments/${paymentId}/refund`);
      setSuccess('Refund issued successfully');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to issue refund');
    } finally {
      setRefundLoading((prev) => ({ ...prev, [paymentId]: false }));
    }
  };

  const getBoothRegistrations = () => {
    const booths = [];
    const isVisible = (b) => b?.registration_status !== 'denied';
    const collect = (source) => {
      if (!Array.isArray(source?.booth_details)) return;
      source.booth_details.forEach((b) => {
        if (b?.booth_name && isVisible(b)) {
          booths.push({
            registration_id: b.registration_id,
            booth_id: b.booth_id,
            name: b.booth_name,
            is_corner: b.is_corner,
            needs_electricity: b.needs_electricity,
            registration_status: b.registration_status,
          });
        }
      });
    };
    collect(vendor);
    relatedVendors.forEach(collect);
    booths.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return booths;
  };

  const getAllBooths = () =>
    getBoothRegistrations().map(({ name, is_corner, needs_electricity }) => ({
      name,
      is_corner,
      needs_electricity,
    }));

  const getOrphanedPayments = () => {
    const activeBoothIds = new Set(
      getBoothRegistrations()
        .filter((b) => ACTIVE_BOOTH_STATUSES.includes(b.registration_status))
        .map((b) => b.booth_id)
        .filter((id) => id !== null && id !== undefined)
    );
    return payments.filter(
      (p) =>
        p.status === 'succeeded' &&
        !p.refund_id &&
        p.booth_id !== null &&
        p.booth_id !== undefined &&
        !activeBoothIds.has(p.booth_id)
    );
  };

  const orphanedPayments = getOrphanedPayments();

  const handleSwapBooth = async (boothData) => {
    if (!changeBoothDialog?.registration_id) return;
    setSwapLoading(true);
    setError('');
    try {
      await api.patch(
        `/admin/booth-registrations/${changeBoothDialog.registration_id}/booth`,
        { boothId: boothData.id }
      );
      setChangeBoothDialog(null);
      setSuccess(`Booth changed to ${boothData.cell_name}. Payment amounts are unchanged.`);
      await refetchVendor();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change booth');
    } finally {
      setSwapLoading(false);
    }
  };

  const canSwapBooth = (booth) =>
    ['approved', 'complete'].includes(booth.registration_status) && booth.registration_id;

  const handleAssignPaidBooth = async (boothData) => {
    setAssignPaidLoading(true);
    setError('');
    try {
      await api.post('/admin/booth-assignments', {
        vendorId: Number(vendorId),
        boothId: boothData.id,
        needsElectricity: Boolean(boothData.needs_electricity),
        reuseExistingPayment: true,
      });
      setSuccess(`Assigned Booth ${boothData.cell_name} using an existing payment (no new charge).`);
      await refetchVendor();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to assign booth');
    } finally {
      setAssignPaidLoading(false);
    }
  };

  const isAdminCreated = isAdminPlaceholderVendor(vendor);

  const openInviteDialog = () => {
    setInviteEmail(vendor?.contact_email || vendor?.email || '');
    setInviteFirstName(vendor?.contact_first_name || vendor?.first_name || '');
    setInviteLastName(vendor?.contact_last_name || vendor?.last_name || '');
    setInviteError('');
    setInviteOpen(true);
  };

  const handleSendInvite = async () => {
    setInviteError('');
    const email = inviteEmail.trim();
    const firstName = inviteFirstName.trim();
    const lastName = inviteLastName.trim();
    if (!email) {
      setInviteError('Email is required');
      return;
    }
    if (!firstName || !lastName) {
      setInviteError('First and last name are required');
      return;
    }
    setInviteLoading(true);
    try {
      const response = await api.post(`/admin/vendors/${vendorId}/create-user`, {
        email,
        firstName,
        lastName,
      });
      setSuccess(response.data?.message || 'Invitation sent');
      setInviteOpen(false);
      await refetchVendor();
    } catch (err) {
      setInviteError(err.response?.data?.error || 'Failed to send invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const openApproveDialog = (markAsPaid = false) => {
    setApproveNotes('');
    setDecisionError('');
    setApproveMarkAsPaid(markAsPaid);
    setApproveOpen(true);
  };

  const openDenyDialog = () => {
    setDenyReason(DENIAL_REASONS[0]);
    setDenyNotes('');
    setDecisionError('');
    setDenyOpen(true);
  };

  const openReopenDialog = () => {
    setReopenNotes('');
    setDecisionError('');
    setReopenOpen(true);
  };

  const handleApproveVendor = async () => {
    setDecisionLoading(true);
    setDecisionError('');
    const wasMarkAsPaid = approveMarkAsPaid;
    try {
      const body = { notes: approveNotes };
      if (wasMarkAsPaid) {
        body.markAsPaid = true;
      }
      const response = await api.post(`/admin/vendors/${vendorId}/approve`, body);
      setApproveOpen(false);
      setApproveMarkAsPaid(false);
      if (response.data?.markAsPaid && response.data?.emailsSent === 0) {
        setSuccess('Vendor approved and marked paid. No confirmation email was sent (no contact email on file).');
      } else if (wasMarkAsPaid) {
        setSuccess('Vendor approved, marked paid, and confirmation email sent.');
      } else {
        setSuccess('Vendor approved and notified.');
      }
      await refetchVendor();
    } catch (err) {
      setDecisionError(err.response?.data?.error || 'Failed to approve vendor');
    } finally {
      setDecisionLoading(false);
    }
  };

  const handleDenyVendor = async () => {
    setDecisionLoading(true);
    setDecisionError('');
    try {
      const boothIds = Array.isArray(vendor?.booth_details)
        ? vendor.booth_details
            .map((b) => b?.booth_id)
            .filter((id) => id !== null && id !== undefined)
        : [];
      await api.post(`/admin/vendors/${vendorId}/deny`, {
        denialReason: denyReason,
        notes: denyNotes,
        boothIds,
      });
      setDenyOpen(false);
      setSuccess('Vendor denied and notified.');
      await refetchVendor();
    } catch (err) {
      setDecisionError(err.response?.data?.error || 'Failed to deny vendor');
    } finally {
      setDecisionLoading(false);
    }
  };

  const handleReopenVendor = async () => {
    setDecisionLoading(true);
    setDecisionError('');
    try {
      await api.post(`/admin/vendors/${vendorId}/reopen`, { notes: reopenNotes });
      setReopenOpen(false);
      setSuccess('Vendor moved back to pending approval. No email was sent.');
      await refetchVendor();
    } catch (err) {
      setDecisionError(err.response?.data?.error || 'Failed to reopen vendor');
    } finally {
      setDecisionLoading(false);
    }
  };

  const contactEmail = getVendorContactEmail(vendor);
  const { primary: identityPrimary, secondary: identitySecondary } = getVendorIdentityLines(vendor);
  const registrationStatus = vendor?.registration_status;
  const canDecide = registrationStatus === 'pending_approval';
  const canReopen = registrationStatus === 'denied';
  const canResendConfirmation =
    registrationStatus === 'approved' || registrationStatus === 'complete';
  const paymentStatus = payments.find((p) => p.status === 'succeeded')
    ? 'succeeded'
    : payments[0]?.status || null;

  const websiteHref = vendor?.website
    ? (vendor.website.startsWith('http') ? vendor.website : `https://${vendor.website}`)
    : null;

  return (
    <Container maxWidth="lg" sx={{ mt: 0, mb: 4 }} disableGutters>
      <Button
        variant="text"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/admin/vendors')}
        sx={{ mb: 2 }}
      >
        Back to Vendors
      </Button>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : vendor ? (
        <Box>
          {/* Header card */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              mb: 3,
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(25,118,210,0.06) 0%, rgba(255,224,102,0.08) 100%)',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {/* Identity row gets the full width so the business name and
                chips never compete with the action buttons for space. */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Avatar
                sx={{
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  width: 56,
                  height: 56,
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {getInitials(identityPrimary || vendor.business_name)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  {identityPrimary}
                </Typography>
                {identitySecondary ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    {identitySecondary}
                  </Typography>
                ) : null}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {contactEmail || 'No contact email'}
                </Typography>
                <Stack direction="row" sx={{ mt: 1.25, flexWrap: 'wrap', gap: 0.75 }}>
                  {vendor.category_name && (
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={
                        vendor.category_slug === 'other' && vendor.category_other_description
                          ? `Other — ${vendor.category_other_description}`
                          : vendor.category_name
                      }
                    />
                  )}
                  {registrationStatus && (
                    <Chip
                      size="small"
                      label={(registrationStatus || '').replace(/_/g, ' ')}
                      color={statusChipColor(registrationStatus)}
                      sx={{ textTransform: 'capitalize' }}
                    />
                  )}
                  {paymentStatus && (
                    <Chip
                      size="small"
                      label={`Payment: ${paymentStatus === 'succeeded' ? 'Completed' : paymentStatus}`}
                      color={paymentChipColor(paymentStatus)}
                      sx={{ textTransform: 'capitalize' }}
                    />
                  )}
                  {isAdminCreated && (
                    <Chip size="small" label="Admin-created" variant="outlined" color="info" />
                  )}
                  {relatedVendors.length > 0 && (
                    <Chip
                      size="small"
                      label={`+${relatedVendors.length} related`}
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Box>
            </Box>

            {/* Action toolbar — primary status actions on the left, Edit
                in the middle, and less-used actions in a More menu. */}
            <Divider sx={{ my: 2 }} />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              {canDecide && (
                <>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => openApproveDialog(false)}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<PaidIcon />}
                    onClick={() => openApproveDialog(true)}
                  >
                    Approve & Mark Paid
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<CancelIcon />}
                    onClick={openDenyDialog}
                  >
                    Deny
                  </Button>
                </>
              )}
              {canReopen && (
                <Tooltip title="Move this vendor back to pending approval. No email is sent.">
                  <Button
                    variant="contained"
                    color="warning"
                    size="small"
                    startIcon={<RestartAltIcon />}
                    onClick={openReopenDialog}
                  >
                    Reopen review
                  </Button>
                </Tooltip>
              )}
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={() => setEditOpen(true)}
              >
                Edit
              </Button>

              <Box sx={{ flex: 1 }} />

              <Tooltip title="More actions">
                <IconButton
                  size="small"
                  onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
                  aria-haspopup="true"
                  aria-expanded={Boolean(moreMenuAnchor)}
                  aria-label="more vendor actions"
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={moreMenuAnchor}
                open={Boolean(moreMenuAnchor)}
                onClose={() => setMoreMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {canResendConfirmation && (
                  <MenuItem
                    onClick={() => {
                      setMoreMenuAnchor(null);
                      handleResendConfirmation();
                    }}
                    disabled={resendLoading}
                  >
                    <RefreshIcon fontSize="small" sx={{ mr: 1.5 }} />
                    {resendLoading ? 'Sending confirmation…' : 'Resend confirmation email'}
                  </MenuItem>
                )}
                {isAdminCreated && (
                  <MenuItem
                    onClick={() => {
                      setMoreMenuAnchor(null);
                      openInviteDialog();
                    }}
                  >
                    <SendIcon fontSize="small" sx={{ mr: 1.5 }} />
                    Invite vendor to claim account
                  </MenuItem>
                )}
                {!canResendConfirmation && !isAdminCreated && (
                  <MenuItem disabled>No additional actions</MenuItem>
                )}
              </Menu>
            </Box>
          </Paper>

          {/* Info grid */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={sectionCardSx}>
                <SectionHeader icon={<StorefrontIcon color="primary" />} title="Business Information" />
                <Field label="Business Name" value={vendor.business_name} />
                <Field
                  label="Primary Craft Category"
                  value={
                    vendor.category_slug === 'other' && vendor.category_other_description
                      ? `Other — ${vendor.category_other_description}`
                      : vendor.category_name
                  }
                />
                <Field label="Type of Exhibit" value={vendor.exhibit_type} />
                <Field label="Items You'll Be Selling" value={vendor.description} />
                <Field label="Website" value={vendor.website} href={websiteHref} />
                {!vendor.description && !vendor.website && (
                  <Typography variant="body2" color="text.secondary">No additional business details.</Typography>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={sectionCardSx}>
                <SectionHeader icon={<PhoneIcon color="primary" />} title="Contact Information" />
                <Field
                  label="Phone"
                  value={vendor.phone}
                  href={vendor.phone ? `tel:${vendor.phone}` : null}
                />
                <Field
                  label="Email"
                  value={contactEmail}
                  href={contactEmail ? `mailto:${contactEmail}` : null}
                />
                {!vendor.phone && !contactEmail && (
                  <Typography variant="body2" color="text.secondary">No contact information on file.</Typography>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={sectionCardSx}>
                <SectionHeader icon={<LocationOnIcon color="primary" />} title="Address" />
                {(vendor.address || vendor.city || vendor.state || vendor.zip_code) ? (
                  <Box>
                    {vendor.address && (
                      <Typography variant="body1">{vendor.address}</Typography>
                    )}
                    <Typography variant="body1">
                      {[vendor.city, vendor.state].filter(Boolean).join(', ')}
                      {vendor.zip_code ? ` ${vendor.zip_code}` : ''}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">No address on file.</Typography>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={sectionCardSx}>
                <SectionHeader icon={<EventSeatIcon color="primary" />} title="Booth Information" />
                {orphanedPayments.length > 0 ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    This vendor has {orphanedPayments.length} succeeded payment
                    {orphanedPayments.length === 1 ? '' : 's'} not linked to an active booth
                    (likely from a previous release). Assign new booths without charging again.
                  </Alert>
                ) : null}
                {orphanedPayments.length > 0 ? (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<SwapHorizIcon fontSize="small" />}
                    onClick={() => setAssignPaidDialogOpen(true)}
                    sx={{ mb: getBoothRegistrations().length > 0 ? 2 : 0 }}
                  >
                    Assign booth using existing payment
                    {orphanedPayments.length > 1
                      ? ` (${orphanedPayments.length} available)`
                      : ''}
                  </Button>
                ) : null}
                {getBoothRegistrations().length > 0 ? (
                  <Stack spacing={1.5}>
                    {getBoothRegistrations().map((booth) => (
                      <Stack
                        key={`${booth.registration_id || booth.name}-${booth.name}`}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ flexWrap: 'wrap', gap: 1 }}
                      >
                        <BoothChip
                          name={booth.name}
                          isCorner={booth.is_corner}
                          needsElectricity={booth.needs_electricity}
                          size="medium"
                        />
                        {canSwapBooth(booth) ? (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<SwapHorizIcon fontSize="small" />}
                            onClick={() => setChangeBoothDialog(booth)}
                          >
                            Swap booth
                          </Button>
                        ) : null}
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">No booths assigned.</Typography>
                )}
              </Paper>
            </Grid>

            {/* Product photos submitted by the vendor for this festival */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={sectionCardSx}>
                <SectionHeader icon={<PhotoLibraryIcon color="primary" />} title="Product Photos" />
                {Array.isArray(vendor.product_photos) && vendor.product_photos.length > 0 ? (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                      gap: 1.5,
                    }}
                  >
                    {vendor.product_photos.map((photo) => {
                      const url = resolvePhotoUrl(photo);
                      return (
                        <a
                          key={photo.id}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'block',
                            borderRadius: 8,
                            overflow: 'hidden',
                            border: '1px solid rgba(0,0,0,0.12)',
                          }}
                        >
                          <img
                            src={url}
                            alt={photo.original_name || 'Product photo'}
                            loading="lazy"
                            style={{
                              width: '100%',
                              height: 140,
                              objectFit: 'cover',
                              display: 'block',
                            }}
                          />
                        </a>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No product photos submitted for this festival.
                  </Typography>
                )}
              </Paper>
            </Grid>

            {/* Payments */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ ...sectionCardSx, p: 0 }}>
                <Box sx={{ p: 3, pb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Payments</Typography>
                </Box>
                <Divider />
                <Box sx={{ p: paymentsLoading || paymentsError || payments.length === 0 ? 3 : 0 }}>
                  {paymentsLoading ? (
                    <CircularProgress size={24} />
                  ) : paymentsError ? (
                    <Alert severity="error">{paymentsError}</Alert>
                  ) : payments.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Booth</TableCell>
                            <TableCell>Stripe ID</TableCell>
                            <TableCell align="right">Refund</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {payments.map((payment) => (
                            <TableRow key={payment.id} hover>
                              <TableCell>{new Date(payment.created_at).toLocaleString()}</TableCell>
                              <TableCell>${Number(payment.amount).toFixed(2)}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={`${payment.status}${payment.refund_id ? ' (refunded)' : ''}`}
                                  color={payment.refund_id ? 'default' : paymentChipColor(payment.status)}
                                  sx={{ textTransform: 'capitalize' }}
                                />
                              </TableCell>
                              <TableCell>{payment.booth_number || 'N/A'}</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                {payment.stripe_payment_intent_id}
                              </TableCell>
                              <TableCell align="right">
                                {payment.status === 'succeeded' && !payment.refund_id ? (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    disabled={refundLoading[payment.id]}
                                    onClick={() => handleRefund(payment.id)}
                                  >
                                    {refundLoading[payment.id] ? <CircularProgress size={16} /> : 'Refund'}
                                  </Button>
                                ) : payment.refund_id ? (
                                  <Typography variant="body2" color="text.secondary">Refunded</Typography>
                                ) : null}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No payments found.</Typography>
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Email history */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ ...sectionCardSx, p: 0 }}>
                <Box sx={{ p: 3, pb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MailOutlineIcon color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Email History</Typography>
                </Box>
                <Divider />
                <Box sx={{ p: emailsLoading || emailsError || emailHistory.length === 0 ? 3 : 0 }}>
                  {emailsLoading ? (
                    <CircularProgress size={24} />
                  ) : emailsError ? (
                    <Alert severity="error">{emailsError}</Alert>
                  ) : emailHistory.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date Sent</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Recipient</TableCell>
                            <TableCell>Subject</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Delivered</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {emailHistory.map((email) => (
                            <TableRow key={email.id} hover>
                              <TableCell>{new Date(email.sent_at).toLocaleString()}</TableCell>
                              <TableCell>
                                <Chip
                                  label={email.email_type.replace('_', ' ')}
                                  size="small"
                                  color={
                                    email.email_type === 'booth_confirmation' ? 'primary'
                                      : email.email_type === 'approval' ? 'success'
                                      : email.email_type === 'denial' ? 'error'
                                      : 'default'
                                  }
                                />
                              </TableCell>
                              <TableCell>{email.recipient_email}</TableCell>
                              <TableCell>{email.subject}</TableCell>
                              <TableCell>
                                <Chip
                                  label={email.status}
                                  size="small"
                                  color={
                                    email.status === 'delivered' ? 'success'
                                      : email.status === 'bounced' ? 'error'
                                      : email.status === 'sent' ? 'warning'
                                      : 'default'
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                {email.delivered_at ? (
                                  <Typography variant="body2" color="success.main">
                                    {new Date(email.delivered_at).toLocaleString()}
                                  </Typography>
                                ) : email.bounced_at ? (
                                  <Typography variant="body2" color="error.main">
                                    Bounced: {new Date(email.bounced_at).toLocaleString()}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">Pending</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No emails sent yet.</Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      ) : null}

      {/* Edit dialog (AdminVendorRegistration renders its own MUI Dialog) */}
      {vendor && (
        <AdminVendorRegistration
          open={editOpen}
          mode="edit"
          vendor={vendor}
          onClose={(shouldRefresh) => {
            setEditOpen(false);
            if (shouldRefresh) refetchVendor();
          }}
        />
      )}

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onClose={() => !inviteLoading && setInviteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite Vendor</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Send a password-setup email to claim this admin-created vendor account.
            The recipient will receive an email to set their password and link their user account to this vendor.
          </Typography>
          {inviteError && <Alert severity="error" sx={{ mb: 2 }}>{inviteError}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              required
              autoFocus
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              InputProps={{ startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="First Name"
                fullWidth
                required
                value={inviteFirstName}
                onChange={(e) => setInviteFirstName(e.target.value)}
              />
              <TextField
                label="Last Name"
                fullWidth
                required
                value={inviteLastName}
                onChange={(e) => setInviteLastName(e.target.value)}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)} disabled={inviteLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSendInvite}
            disabled={inviteLoading}
            startIcon={inviteLoading ? <CircularProgress size={16} /> : <SendIcon />}
          >
            {inviteLoading ? 'Sending…' : 'Send Invite'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve dialog */}
      <Dialog
        open={approveOpen}
        onClose={() => {
          if (!decisionLoading) {
            setApproveOpen(false);
            setApproveMarkAsPaid(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {approveMarkAsPaid ? 'Approve & Mark Paid' : 'Approve'} {vendor?.business_name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {approveMarkAsPaid
              ? 'Marks all pending booth registrations as approved and paid, records manual payments, and sends a confirmation email (not a payment request). Skips the 14-day online payment window.'
              : 'This approves all booth registrations for this vendor for the current festival and emails the vendor.'}
          </Typography>
          {approveMarkAsPaid ? (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {getVendorContactEmail(vendor)
                ? `Confirmation email will be sent to ${getVendorContactEmail(vendor)}.`
                : 'No contact email on file — registration will be completed without sending an email.'}
            </Typography>
          ) : null}
          {decisionError && <Alert severity="error" sx={{ mb: 2 }}>{decisionError}</Alert>}
          <TextField
            label="Notes (optional)"
            value={approveNotes}
            onChange={(e) => setApproveNotes(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setApproveOpen(false);
              setApproveMarkAsPaid(false);
            }}
            disabled={decisionLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApproveVendor}
            variant="contained"
            color={approveMarkAsPaid ? 'primary' : 'success'}
            disabled={decisionLoading}
            startIcon={
              decisionLoading ? (
                <CircularProgress size={16} />
              ) : approveMarkAsPaid ? (
                <PaidIcon />
              ) : (
                <CheckCircleIcon />
              )
            }
          >
            {approveMarkAsPaid ? 'Approve & Mark Paid' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deny dialog */}
      <Dialog
        open={denyOpen}
        onClose={() => !decisionLoading && setDenyOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Deny {vendor?.business_name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Denies every booth registration for this vendor. The vendor will be emailed with the reason below.
          </Typography>
          {decisionError && <Alert severity="error" sx={{ mb: 2 }}>{decisionError}</Alert>}
          <TextField
            select
            label="Reason"
            value={denyReason}
            onChange={(e) => setDenyReason(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          >
            {DENIAL_REASONS.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Internal notes (optional)"
            value={denyNotes}
            onChange={(e) => setDenyNotes(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDenyOpen(false)} disabled={decisionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDenyVendor}
            variant="contained"
            color="error"
            disabled={decisionLoading}
            startIcon={decisionLoading ? <CircularProgress size={16} /> : <CancelIcon />}
          >
            Deny
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reopen (un-deny) dialog */}
      <Dialog
        open={reopenOpen}
        onClose={() => !decisionLoading && setReopenOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reopen {vendor?.business_name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Moves this vendor (and all of its denied booth registrations) back to pending approval.
            The original denial reason will be preserved in the admin notes for audit.
            The vendor will <strong>not</strong> be emailed — they will only hear from us when an admin makes a new decision.
          </Typography>
          {decisionError && <Alert severity="error" sx={{ mb: 2 }}>{decisionError}</Alert>}
          <TextField
            label="Internal note (optional)"
            value={reopenNotes}
            onChange={(e) => setReopenNotes(e.target.value)}
            multiline
            rows={3}
            fullWidth
            helperText="Appended to admin notes alongside the reopen timestamp."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReopenOpen(false)} disabled={decisionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleReopenVendor}
            variant="contained"
            color="warning"
            disabled={decisionLoading}
            startIcon={decisionLoading ? <CircularProgress size={16} /> : <RestartAltIcon />}
          >
            Reopen review
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!changeBoothDialog}
        onClose={() => !swapLoading && setChangeBoothDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Swap booth for {vendor?.business_name}
          {changeBoothDialog?.name ? ` (currently ${changeBoothDialog.name})` : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pick a new booth on the map. Registration status and payment amounts stay the same — handle any price difference in person at the event.
          </Typography>
          <BoothSelectionMap onSelect={handleSwapBooth} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeBoothDialog(null)} disabled={swapLoading}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={assignPaidDialogOpen}
        onClose={() => !assignPaidLoading && setAssignPaidDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Assign booth using existing payment — {vendor?.business_name}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {orphanedPayments.length} unused payment{orphanedPayments.length === 1 ? '' : 's'}{' '}
            on file. Click an available booth on the map for each one — the vendor will not be
            charged again. Repeat until all payments are linked.
          </Typography>
          <BoothSelectionMap onSelect={handleAssignPaidBooth} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignPaidDialogOpen(false)} disabled={assignPaidLoading}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default VendorDetailsPage;
